"use client"

import { CloseIcon, SearchIcon } from "@/icons"
import { useParams, useRouter, useSearchParams } from "next/navigation"
import { useEffect, useRef, useState } from "react"
import { useTranslate } from "@tolgee/react"

export const BarebonesSearch = () => {
  const { t } = useTranslate()
  const router = useRouter()
  const { locale } = useParams()
  const searchParams = useSearchParams()
  const inputRef = useRef<HTMLInputElement>(null)

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState(searchParams.get("query") || "")

  useEffect(() => {
    if (!open) return
    const id = window.requestAnimationFrame(() => inputRef.current?.focus())
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    window.addEventListener("keydown", onKey)
    return () => {
      window.cancelAnimationFrame(id)
      window.removeEventListener("keydown", onKey)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const country = String(locale || "")
    const q = search.trim()
    const path = q
      ? `/${country}/categories?query=${encodeURIComponent(q)}`
      : `/${country}/categories`
    setOpen(false)
    router.push(path)
  }

  return (
    <>
      <button
        type="button"
        aria-label={t("barebones_nav.search", "Search")}
        aria-expanded={open}
        className="bb-nav-trigger inline-flex h-8 w-8 items-center justify-center text-[#2b2118] transition-opacity hover:opacity-70"
        onClick={() => setOpen(true)}
      >
        <SearchIcon size={20} color="currentColor" />
      </button>

      <div
        className={`bb-search-overlay ${open ? "bb-search-overlay--open" : ""}`}
        aria-hidden={!open}
      >
        <button
          type="button"
          tabIndex={open ? 0 : -1}
          aria-label={t("barebones_nav.search_close", "Close search")}
          className="bb-search-backdrop"
          onClick={() => setOpen(false)}
        />
        <div className="bb-search-bar" role="dialog" aria-modal="true">
          <form
            className="bb-search-form"
            method="GET"
            onSubmit={submitHandler}
          >
            <input
              ref={inputRef}
              type="search"
              name="query"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("barebones_nav.search_placeholder", "Search...")}
              className="bb-search-input"
              autoComplete="off"
              tabIndex={open ? 0 : -1}
            />
            <button
              type="button"
              aria-label={t("barebones_nav.search_close", "Close")}
              tabIndex={open ? 0 : -1}
              className="bb-search-close"
              onClick={() => setOpen(false)}
            >
              <CloseIcon size={18} color="currentColor" />
            </button>
          </form>
        </div>
      </div>
    </>
  )
}
